/**
 * Admin App — operator surfaces.
 *
 * Single navigation: V2 Marketplace Command Center. Legacy /admin/* URLs
 * are kept as 301-style redirects to their V2 equivalents so any
 * bookmarks survive, but the OLD `AdminTopNav` is no longer mounted
 * anywhere — every admin surface now renders inside the V2 `AdminShell`.
 *
 * `GET /admin` → `/admin/v2/command`.
 */
import React, { useCallback } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import ProtectedRoute from "@/shared/components/ProtectedRoute";
import StandaloneRedirect from "@/shared/components/StandaloneRedirect";
import { RouterTransition } from "@/shared/motion";

import AdminSubdomainRedirect from "@/apps/admin/AdminSubdomainRedirect";

import CommandCenter from "@/apps/admin/v2/pages/CommandCenter";
import MarketplacePage from "@/apps/admin/v2/pages/MarketplacePage";
import SectorManager from "@/apps/admin/v2/pages/SectorManager";
import {
  OperationsPage,
  AvailabilityPage,
  CustomersPage,
  DriversPage,
  WaitlistPage,
  FinancePage,
  BlocksLegacyPage,
  SettingsHomePage,
} from "@/apps/admin/v2/pages/PhasePages";

function P({ children }) {
  return <ProtectedRoute adminOnly>{children}</ProtectedRoute>;
}

export default function AdminApp() {
  const renderRoutes = useCallback((loc) => (
      <Routes location={loc}>
        {/* V2 — the only admin */}
        <Route path="/admin" element={<Navigate to="/admin/v2/command" replace />} />
        <Route path="/admin/v2" element={<Navigate to="/admin/v2/command" replace />} />

        <Route path="/admin/v2/command"          element={<P><CommandCenter /></P>} />
        <Route path="/admin/v2/marketplace"      element={<P><MarketplacePage /></P>} />
        <Route path="/admin/v2/operations"       element={<P><OperationsPage /></P>} />
        <Route path="/admin/v2/finance"          element={<P><FinancePage /></P>} />
        <Route path="/admin/v2/drivers"          element={<P><DriversPage /></P>} />
        <Route path="/admin/v2/customers"        element={<P><CustomersPage /></P>} />
        <Route path="/admin/v2/waitlist"         element={<P><WaitlistPage /></P>} />
        <Route path="/admin/v2/availability"     element={<P><AvailabilityPage /></P>} />
        <Route path="/admin/v2/blocks"           element={<P><BlocksLegacyPage /></P>} />
        <Route path="/admin/v2/settings"         element={<P><SettingsHomePage /></P>} />
        <Route path="/admin/v2/settings/sectors" element={<P><SectorManager /></P>} />

        {/* Legacy redirects — operators with bookmarks land in the V2 shell. */}
        <Route path="/admin/home"              element={<Navigate to="/admin/v2/command" replace />} />
        <Route path="/admin/dashboard"         element={<Navigate to="/admin/v2/drivers" replace />} />
        <Route path="/admin/ops"               element={<Navigate to="/admin/v2/operations" replace />} />
        <Route path="/admin/availability"      element={<Navigate to="/admin/v2/availability" replace />} />
        <Route path="/admin/customers"         element={<Navigate to="/admin/v2/customers" replace />} />
        <Route path="/admin/waitlist"          element={<Navigate to="/admin/v2/waitlist" replace />} />
        <Route path="/admin/checkout-failures" element={<Navigate to="/admin/v2/finance" replace />} />
        <Route path="/admin/blocks"            element={<Navigate to="/admin/v2/marketplace" replace />} />

        {/* Catch-all under /admin keeps users in the V2 shell instead of a 404. */}
        <Route path="/admin/*" element={<Navigate to="/admin/v2/command" replace />} />
      </Routes>
  ), []);
  return (
    <>
      <StandaloneRedirect />
      <AdminSubdomainRedirect />
      <RouterTransition routes={renderRoutes} />
    </>
  );
}
